import { useState, useRef, useCallback, useEffect } from 'react';
import useSWR from 'swr';
import { useTenant } from '@/context/TenantContext.tsx';
import api from '@/lib/api';
import DocumentVerifyModal from './DocumentVerifyModal';

// ── Types ────────────────────────────────────────────────────────────────────

interface Category {
  id: string;
  nombre: string;
  slug: string;
  color: string;
  doc_count: number;
}

interface FieldValue {
  value: string;
  status: 'validated' | 'dubious';
  reason?: string;
}

interface DocumentMetadata {
  numero_factura?:  FieldValue;
  fecha_factura?:   FieldValue;
  empresa_emisora?: FieldValue;
  cif_emisor?:      FieldValue;
  base_imponible?:  FieldValue;
  iva?:             FieldValue;
  total?:           FieldValue;
  trabajador?:      FieldValue;
  periodo?:         FieldValue;
  salario_bruto?:   FieldValue;
  deducciones?:     FieldValue;
  liquido?:         FieldValue;
  tipo_contrato?:   FieldValue;
  fecha_inicio?:    FieldValue;
  fecha_fin?:       FieldValue;
  [key: string]: FieldValue | undefined;
}

interface Document {
  id: string;
  nombre: string;
  filename: string;
  mimetype: string;
  size_bytes: number;
  ocr_status: 'pending' | 'processing' | 'done' | 'error' | 'skipped';
  validation_status: 'ai_processed' | 'verified';
  category_id: string | null;
  category_nombre: string | null;
  category_color: string | null;
  employee_nombre: string | null;
  metadata: DocumentMetadata | null;
  created_at: string;
}

// ── Fetcher ──────────────────────────────────────────────────────────────────

const fetcher = (url: string) => api.get(url).then(r => r.data);

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── OCR Status Badge ─────────────────────────────────────────────────────────

function OcrBadge({ status, validationStatus }: { status: Document['ocr_status']; validationStatus?: Document['validation_status'] }) {
  // Si está verificado, mostrar badge especial
  if (validationStatus === 'verified') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
        background: 'rgba(16,185,129,0.15)', color: '#10b981',
        border: '1px solid rgba(16,185,129,0.3)',
      }}>
        <span style={{ fontSize: 10 }}>✓</span> Verificado
      </span>
    );
  }

  const map: Record<Document['ocr_status'], { label: string; style: React.CSSProperties }> = {
    pending:    { label: 'Pendiente',   style: { background: 'rgba(100,116,139,0.2)', color: '#94a3b8' } },
    processing: { label: 'Procesando', style: { background: 'rgba(245,158,11,0.15)', color: '#f59e0b' } },
    done:       { label: 'Listo (IA)', style: { background: 'rgba(34,197,94,0.15)',  color: '#22c55e' } },
    error:      { label: 'Error OCR',  style: { background: 'rgba(239,68,68,0.15)', color: '#ef4444' } },
    skipped:    { label: 'Sin OCR',    style: { background: 'rgba(99,102,241,0.15)', color: '#818cf8' } },
  };
  const entry = map[status] ?? { label: status ?? '—', style: { background: 'rgba(100,116,139,0.2)', color: '#94a3b8' } };
  const { label, style } = entry;

  if (status === 'processing') {
    return (
      <span style={{
        ...style,
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          border: '2px solid #f59e0b', borderTopColor: 'transparent',
          display: 'inline-block',
          animation: 'spin 0.8s linear infinite',
        }} />
        {label}
      </span>
    );
  }
  return (
    <span style={{
      ...style,
      display: 'inline-block', padding: '2px 10px',
      borderRadius: 999, fontSize: 12, fontWeight: 500,
    }}>
      {label}
    </span>
  );
}

// ── Metadata Panel con semáforo y edición inline ─────────────────────────────

const LABEL_MAP: Record<string, string> = {
  numero_factura:  'N° Factura',
  fecha_factura:   'Fecha',
  empresa_emisora: 'Empresa emisora',
  cif_emisor:      'CIF / NIF emisor',
  base_imponible:  'Base imponible',
  tipo_iva:        'Tipo IVA %',
  iva:             'Importe IVA',
  total:           'Total',
  trabajador:      'Trabajador',
  periodo:         'Periodo',
  salario_bruto:   'Salario bruto',
  deducciones:     'Deducciones',
  liquido:         'Líquido neto',
  tipo_contrato:   'Tipo contrato',
  fecha_inicio:    'Fecha inicio',
  fecha_fin:       'Fecha fin',
};

// Campos obligatorios por tipo de documento
const REQUIRED_INVOICE_FIELDS = [
  'empresa_emisora',
  'cif_emisor',
  'numero_factura',
  'fecha_factura',
  'base_imponible',
  'tipo_iva',
  'iva',
  'total',
] as const;

// Opciones para el tipo de IVA
const TIPO_IVA_OPTIONS = ['0', '4', '10', '21'] as const;

interface MetadataPanelProps {
  doc: Document;
  colSpan: number;
  onVerify: (id: string) => Promise<void>;
  onSaveField: (id: string, field: string, value: string) => Promise<void>;
}

function MetadataPanel({ doc, colSpan, onVerify, onSaveField }: MetadataPanelProps) {
  const { metadata, validation_status } = doc;
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [verifying, setVerifying] = useState(false);

  // Detectar si es una factura (tiene algún campo de factura o no tiene categoría asignada)
  const isInvoice = metadata && (
    metadata.numero_factura !== undefined ||
    metadata.base_imponible !== undefined ||
    metadata.total !== undefined ||
    !doc.category_id  // Sin categoría, asumimos factura
  );

  // Para facturas, mostrar TODOS los campos obligatorios (incluso vacíos)
  // Para otros documentos, mostrar solo los campos con valor
  const fieldsToShow: [string, FieldValue | undefined][] = isInvoice
    ? REQUIRED_INVOICE_FIELDS.map(key => [key, metadata?.[key]])
    : metadata
      ? Object.entries(metadata).filter(([, v]) => v?.value) as [string, FieldValue][]
      : [];

  // Si no hay campos que mostrar, no renderizar el panel
  if (!fieldsToShow.length && !isInvoice) return null;

  const isVerified = validation_status === 'verified';

  // Calcular campos con valor (detectados o editados)
  const getFieldValue = (key: string): string => {
    if (editValues[key] !== undefined) return editValues[key];
    const field = metadata?.[key];
    return field?.value ?? '';
  };

  // Verificar si faltan campos obligatorios para permitir confirmar
  const missingRequiredFields = isInvoice
    ? REQUIRED_INVOICE_FIELDS.filter(key => !getFieldValue(key))
    : [];
  const canConfirm = missingRequiredFields.length === 0;

  // Determinar estado visual
  const hasDubious = fieldsToShow.some(([, f]) => f?.status === 'dubious');
  const hasMissing = missingRequiredFields.length > 0;

  const handleVerify = async () => {
    if (!canConfirm) return;
    setVerifying(true);
    try { await onVerify(doc.id); } catch { /* handled in parent */ }
    finally { setVerifying(false); }
  };

  // Guardar campo editado
  const saveField = async (key: string, value: string) => {
    await onSaveField(doc.id, key, value);
    setEditValues(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: '0 16px 16px 40px', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{
          borderRadius: 8, overflow: 'hidden',
          border: `1px solid ${(hasDubious || hasMissing) && !isVerified ? 'rgba(245,158,11,0.4)' : 'var(--color-border)'}`,
          borderTop: `2px solid ${isVerified ? '#10b981' : (hasDubious || hasMissing) ? '#f59e0b' : '#22c55e'}`,
        }}>
          {/* Header semáforo */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 16px',
            background: isVerified
              ? 'rgba(16,185,129,0.08)'
              : (hasDubious || hasMissing) ? 'rgba(245,158,11,0.06)' : 'rgba(34,197,94,0.06)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: isVerified ? '#10b981' : (hasDubious || hasMissing) ? '#f59e0b' : '#22c55e',
                boxShadow: `0 0 6px ${isVerified ? '#10b981' : (hasDubious || hasMissing) ? '#f59e0b' : '#22c55e'}`,
              }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: isVerified ? '#10b981' : (hasDubious || hasMissing) ? '#f59e0b' : '#22c55e' }}>
                {isVerified
                  ? 'Datos verificados por humano'
                  : hasMissing
                    ? `Faltan ${missingRequiredFields.length} campo(s) obligatorio(s)`
                    : hasDubious
                      ? 'Algunos campos necesitan revisión'
                      : 'Datos extraídos con alta confianza'}
              </span>
            </div>
            {!isVerified && (
              <button
                onClick={handleVerify}
                disabled={verifying || !canConfirm}
                title={!canConfirm ? `Faltan: ${missingRequiredFields.map(k => LABEL_MAP[k] || k).join(', ')}` : ''}
                style={{
                  padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: canConfirm ? 'pointer' : 'not-allowed',
                  background: canConfirm ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)',
                  border: `1px solid ${canConfirm ? 'rgba(16,185,129,0.4)' : 'rgba(100,116,139,0.3)'}`,
                  color: canConfirm ? '#10b981' : '#64748b',
                  opacity: verifying ? 0.6 : 1,
                }}
              >
                {verifying ? 'Confirmando...' : canConfirm ? 'Confirmar datos' : 'Completar campos'}
              </button>
            )}
          </div>

          {/* Grid de campos */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '1px',
            background: 'var(--color-border)',
          }}>
            {fieldsToShow.map(([key, field]) => {
              const currentValue = getFieldValue(key);
              const isEmpty = !currentValue;
              const isDubious = field?.status === 'dubious';
              const isRequired = isInvoice && (REQUIRED_INVOICE_FIELDS as readonly string[]).includes(key);
              const isTipoIva = key === 'tipo_iva';

              return (
                <div key={key} style={{
                  background: isEmpty
                    ? 'rgba(239,68,68,0.05)'  // Rojo suave para campos vacíos
                    : isDubious && !isVerified
                      ? 'rgba(245,158,11,0.05)'
                      : 'var(--color-surface)',
                  padding: '10px 14px',
                  position: 'relative',
                }}>
                  {/* Indicador semáforo lateral */}
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
                    background: isEmpty
                      ? '#ef4444'  // Rojo para campos vacíos obligatorios
                      : isDubious && !isVerified ? '#f59e0b' : '#22c55e',
                    borderRadius: '0 0 0 0',
                  }} />

                  <div style={{
                    fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: isEmpty ? '#ef4444' : 'var(--color-muted)',
                    marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    {LABEL_MAP[key] ?? key}
                    {isRequired && isEmpty && <span style={{ color: '#ef4444' }}>*</span>}
                  </div>

                  {isVerified ? (
                    <div style={{ fontSize: 13, color: 'var(--color-text)', fontWeight: 500 }}>
                      {currentValue || '—'}
                    </div>
                  ) : isTipoIva ? (
                    // Selector especial para tipo de IVA
                    <select
                      value={currentValue}
                      onChange={e => {
                        setEditValues(prev => ({ ...prev, [key]: e.target.value }));
                        saveField(key, e.target.value);
                      }}
                      style={{
                        width: '100%', fontSize: 13, fontWeight: 500,
                        background: 'var(--color-surface)',
                        border: `1px solid ${isEmpty ? 'rgba(239,68,68,0.5)' : 'var(--color-border)'}`,
                        borderRadius: 4, padding: '4px 8px', color: 'var(--color-text)',
                        outline: 'none', cursor: 'pointer',
                      }}
                    >
                      <option value="">Seleccionar...</option>
                      {TIPO_IVA_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}%</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={currentValue}
                      placeholder={isEmpty ? 'Introducir valor...' : ''}
                      onChange={e => setEditValues(prev => ({ ...prev, [key]: e.target.value }))}
                      onBlur={async () => {
                        const newVal = editValues[key];
                        if (newVal !== undefined && newVal !== (field?.value ?? '')) {
                          await saveField(key, newVal);
                        }
                      }}
                      style={{
                        width: '100%', fontSize: 13, fontWeight: 500,
                        background: 'transparent',
                        border: `1px solid ${isEmpty ? 'rgba(239,68,68,0.5)' : isDubious ? 'rgba(245,158,11,0.5)' : 'transparent'}`,
                        borderRadius: 4, padding: '2px 6px', color: 'var(--color-text)',
                        outline: 'none', cursor: 'text',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.15s',
                      }}
                      onFocus={e => { e.target.style.borderColor = 'var(--color-accent)'; }}
                    />
                  )}

                  {/* Razón del campo dudoso */}
                  {isDubious && !isVerified && field?.reason && !isEmpty && (
                    <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 4, lineHeight: 1.3 }}>
                      {field.reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Row Skeleton ────────────────────────��────────────────────────────────────

function SkeletonRow() {
  const cell = (w: string) => (
    <td style={{ padding: '14px 16px' }}>
      <div style={{
        height: 14, width: w, borderRadius: 4,
        background: 'var(--color-skeleton)',
        animation: 'pulse 1.5s ease-in-out infinite',
      }} />
    </td>
  );
  return (
    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
      {cell('60%')}{cell('30%')}{cell('50%')}{cell('40%')}{cell('30%')}{cell('20%')}
    </tr>
  );
}

// ── Upload Modal (individual + masivo) ───────────────────────────────────────

type FileStatus = 'pending' | 'uploading' | 'done' | 'error';
interface QueuedFile { file: File; status: FileStatus; error?: string; }

interface UploadModalProps {
  categories: Category[];
  onClose: () => void;
  onDone: () => void;
}

function UploadModal({ categories, onClose, onDone }: UploadModalProps) {
  const [dragging, setDragging]     = useState(false);
  const [queue, setQueue]           = useState<QueuedFile[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [mode, setMode]             = useState<'single' | 'bulk'>('single');
  const [running, setRunning]       = useState(false);
  const [done, setDone]             = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    setQueue(prev => {
      const names = new Set(prev.map(q => q.file.name));
      const fresh = arr.filter(f => !names.has(f.name)).map(f => ({ file: f, status: 'pending' as FileStatus }));
      return [...prev, ...fresh];
    });
    if (arr.length > 1) setMode('bulk');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const removeFile = (idx: number) => setQueue(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!queue.length || running) return;
    setRunning(true);

    const cat = categories.find(c => c.id === categoryId);

    if (mode === 'bulk' || queue.length > 1) {
      // Subida masiva en un único request
      const form = new FormData();
      queue.forEach(q => form.append('files', q.file));
      if (categoryId)       form.append('category_id',   categoryId);
      if (cat?.slug)        form.append('category_slug', cat.slug);

      setQueue(prev => prev.map(q => ({ ...q, status: 'uploading' })));
      try {
        await api.post('/documents/bulk', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        setQueue(prev => prev.map(q => ({ ...q, status: 'done' })));
        setDone(true);
        onDone();
      } catch {
        setQueue(prev => prev.map(q => ({ ...q, status: 'error', error: 'Error en la subida' })));
      }
    } else {
      // Subida individual (mantiene nombre personalizado)
      const { file } = queue[0];
      setQueue([{ file, status: 'uploading' }]);
      try {
        const form = new FormData();
        form.append('file', file);
        form.append('nombre', file.name);
        if (categoryId) { form.append('category_id', categoryId); if (cat?.slug) form.append('category_slug', cat.slug); }
        await api.post('/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        setQueue([{ file, status: 'done' }]);
        setDone(true);
        onDone();
      } catch {
        setQueue([{ file, status: 'error', error: 'Error en la subida' }]);
      }
    }
    setRunning(false);
  };

  const statusColor: Record<FileStatus, string> = {
    pending:   'var(--color-muted)',
    uploading: '#f59e0b',
    done:      '#10b981',
    error:     '#ef4444',
  };
  const statusLabel: Record<FileStatus, string> = {
    pending: 'Pendiente', uploading: 'Subiendo...', done: 'Listo', error: 'Error',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={!running ? onClose : undefined}>
      <form
        onSubmit={handleSubmit}
        style={{
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 12, padding: 28, width: 560, maxWidth: '96vw',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 17, fontWeight: 600, color: 'var(--color-text)', margin: 0 }}>
            Subir documentos
          </h3>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['single','bulk'] as const).map(m => (
              <button
                key={m} type="button" onClick={() => setMode(m)}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  background: mode === m ? 'var(--color-accent)' : 'transparent',
                  border: `1px solid ${mode === m ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  color: mode === m ? '#fff' : 'var(--color-muted)',
                }}
              >
                {m === 'single' ? 'Individual' : 'Masivo'}
              </button>
            ))}
          </div>
        </div>

        {/* Dropzone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? 'var(--color-accent)' : 'var(--color-border)'}`,
            borderRadius: 8, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
            background: dragging ? 'rgba(14,165,233,0.05)' : 'transparent',
            transition: 'all 0.15s', marginBottom: 16,
          }}
        >
          <input
            ref={inputRef} type="file" multiple={mode === 'bulk'}
            accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff"
            onChange={e => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
          <p style={{ color: 'var(--color-muted)', fontSize: 14, margin: 0 }}>
            {mode === 'bulk' ? 'Arrastra varios archivos o haz clic para seleccionar' : 'Arrastra un archivo o haz clic para seleccionar'}
          </p>
          <p style={{ color: 'var(--color-muted)', fontSize: 12, marginTop: 4 }}>
            PDF, JPG, PNG, WEBP, TIFF — Max 25 MB por archivo
          </p>
        </div>

        {/* Cola de archivos */}
        {queue.length > 0 && (
          <div style={{
            border: '1px solid var(--color-border)', borderRadius: 8,
            overflow: 'hidden', marginBottom: 16, maxHeight: 220, overflowY: 'auto',
          }}>
            {queue.map((q, i) => (
              <div key={q.file.name + i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 14px',
                borderBottom: i < queue.length - 1 ? '1px solid var(--color-border)' : 'none',
                background: q.status === 'error' ? 'rgba(239,68,68,0.05)' : q.status === 'done' ? 'rgba(16,185,129,0.04)' : 'transparent',
              }}>
                <span style={{ fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-text)' }}>
                  {q.file.name}
                </span>
                <span style={{ fontSize: 11, color: 'var(--color-muted)', whiteSpace: 'nowrap' }}>
                  {formatBytes(q.file.size)}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  color: statusColor[q.status],
                }}>
                  {q.status === 'uploading'
                    ? <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '2px solid #f59e0b', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
                    : statusLabel[q.status]
                  }
                </span>
                {q.status === 'pending' && (
                  <button type="button" onClick={() => removeFile(i)} style={{
                    background: 'none', border: 'none', color: 'var(--color-muted)',
                    cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 2px',
                  }}>×</button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Categoría */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--color-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
            Categoría (opcional)
          </label>
          <select
            value={categoryId} onChange={e => setCategoryId(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 6, fontSize: 14,
              background: 'var(--color-bg)', border: '1px solid var(--color-border)',
              color: 'var(--color-text)', outline: 'none',
            }}
          >
            <option value="">Sin categoría (el OCR la asignará)</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>

        {/* Resumen y acciones */}
        {done && (
          <p style={{ fontSize: 13, color: '#10b981', marginBottom: 12 }}>
            {queue.filter(q => q.status === 'done').length} archivo{queue.length !== 1 ? 's' : ''} subido{queue.length !== 1 ? 's' : ''} correctamente. El OCR se procesara en segundo plano.
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--color-muted)' }}>
            {queue.length > 0 ? `${queue.length} archivo${queue.length !== 1 ? 's' : ''} seleccionado${queue.length !== 1 ? 's' : ''}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} disabled={running} style={{
              padding: '8px 20px', borderRadius: 6, fontSize: 14,
              background: 'transparent', border: '1px solid var(--color-border)',
              color: 'var(--color-muted)', cursor: running ? 'not-allowed' : 'pointer',
            }}>
              {done ? 'Cerrar' : 'Cancelar'}
            </button>
            {!done && (
              <button type="submit" disabled={queue.length === 0 || running} style={{
                padding: '8px 20px', borderRadius: 6, fontSize: 14, fontWeight: 500,
                background: queue.length === 0 || running ? 'var(--color-border)' : 'var(--color-accent)',
                border: 'none', color: '#fff', cursor: queue.length === 0 || running ? 'not-allowed' : 'pointer',
              }}>
                {running ? 'Subiendo...' : queue.length > 1 ? `Subir ${queue.length} archivos` : 'Subir'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

// ── Export dropdown ───────────────────────────────────────────────────────────

function ExportButton({ search, categoryId, dateFrom, dateTo }: {
  search: string;
  categoryId: string;
  dateFrom: string;
  dateTo: string;
}) {
  const [open, setOpen] = useState(false);

  const doExport = (format: 'csv' | 'excel' | 'pdf') => {
    setOpen(false);
    const token = localStorage.getItem('erp_token') ?? '';
    const slug  = sessionStorage.getItem('tenant_slug') ?? '';
    const base  = import.meta.env.VITE_API_URL ?? '/api/v1';
    const params = new URLSearchParams({ format, token, slug });
    if (search)     params.set('search',      search);
    if (categoryId) params.set('category_id', categoryId);
    if (dateFrom)   params.set('date_from',   dateFrom);
    if (dateTo)     params.set('date_to',     dateTo);
    window.open(`${base}/documents/export?${params}`, '_blank');
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 500,
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          color: 'var(--color-text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        Exportar
        <span style={{ fontSize: 10 }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 8, overflow: 'hidden', zIndex: 20, minWidth: 140,
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        }}>
          {([
            { fmt: 'csv'   as const, label: 'CSV (.csv)' },
            { fmt: 'excel' as const, label: 'Excel (.xls)' },
            { fmt: 'pdf'   as const, label: 'PDF / Imprimir' },
          ]).map(({ fmt, label }) => (
            <button
              key={fmt} type="button"
              onClick={() => doExport(fmt)}
              style={{
                display: 'block', width: '100%', padding: '10px 16px',
                textAlign: 'left', fontSize: 13, background: 'none', border: 'none',
                color: 'var(--color-text)', cursor: 'pointer',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function DocumentsPage() {
  const { tenantSlug } = useTenant();
  const [search, setSearch]         = useState('');
  const [filterCat, setFilterCat]   = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [showUpload, setShowUpload]   = useState(false);
  const [pollingIds, setPollingIds]   = useState<Set<string>>(new Set());
  const [selected, setSelected]       = useState<Set<string>>(new Set());
  const [zipping, setZipping]         = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [verifyingDocId, setVerifyingDocId] = useState<string | null>(null);

  const { data: catData } = useSWR<{ data: Category[] }>('/documents/categories', fetcher);
  const categories = catData?.data ?? [];

  const swrKey = `/documents?search=${search}&category_id=${filterCat}&date_from=${dateFrom}&date_to=${dateTo}`;
  const { data, isLoading, mutate } = useSWR<{ data: Document[]; total: number }>(swrKey, fetcher, {
    refreshInterval: pollingIds.size > 0 ? 3000 : 0,
  });
  const documents = data?.data ?? [];

  // Actualizar set de IDs en procesamiento para el polling
  useEffect(() => {
    const processing = new Set(
      documents.filter(d => d.ocr_status === 'pending' || d.ocr_status === 'processing').map(d => d.id)
    );
    setPollingIds(prev => {
      if (
        prev.size === processing.size &&
        [...prev].every(id => processing.has(id))
      ) return prev;
      return processing;
    });
  }, [documents]);

  // ── Selección y descarga ZIP ─────────────────────────────────────────────────
  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === documents.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(documents.map(d => d.id)));
    }
  };

  const handleDownloadZip = async () => {
    if (selected.size === 0 || zipping) return;
    setZipping(true);
    try {
      const response = await api.post(
        '/documents/zip',
        { ids: Array.from(selected) },
        { responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `documentos_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setZipping(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este documento?')) return;
    await api.delete(`/documents/${id}`);
    mutate();
  };

  const handleReprocess = async (id: string) => {
    await api.post(`/documents/${id}/reprocess`);
    mutate();
  };

  const handleVerify = async (id: string) => {
    await api.post(`/documents/${id}/verify`);
    mutate();
  };

  const handleSaveField = async (id: string, field: string, value: string) => {
    if (!field) return;
    await api.patch(`/documents/${id}/metadata`, { [field]: value });
    mutate();
  };

  const handleView = (id: string) => {
    // Abrir el modal de verificacion con vista dividida
    setVerifyingDocId(id);
  };

  // Función para abrir documento en nueva pestaña (usada por el modal)
  const handleViewExternal = useCallback((id: string) => {
    const slug  = tenantSlug ?? sessionStorage.getItem('tenant_slug') ?? '';
    const token = localStorage.getItem('erp_token') ?? '';
    window.open(
      `/api/v1/documents/${id}/view?slug=${encodeURIComponent(slug)}&token=${encodeURIComponent(token)}`,
      '_blank'
    );
  }, [tenantSlug]);

  return (
    <div style={{ }}>
      {/* Cabecera */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
          Documentos
        </h1>
        <p style={{ fontSize: 14, color: 'var(--color-muted)' }}>
          Gestión documental con OCR automático
        </p>
      </div>

      {/* Tarjetas de categorías */}
      {categories.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setFilterCat(prev => prev === cat.id ? '' : cat.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                background: filterCat === cat.id ? `${cat.color}22` : 'var(--color-surface)',
                border: `1px solid ${filterCat === cat.id ? cat.color : 'var(--color-border)'}`,
                color: filterCat === cat.id ? cat.color : 'var(--color-muted)',
                fontSize: 13, fontWeight: 500, transition: 'all 0.15s',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
              {cat.nombre}
              <span style={{
                background: `${cat.color}33`, color: cat.color,
                borderRadius: 999, padding: '0 6px', fontSize: 11, fontWeight: 600,
              }}>
                {cat.doc_count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nombre o texto OCR..."
          style={{
            flex: '1 1 200px', padding: '8px 14px', borderRadius: 6, fontSize: 14,
            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            color: 'var(--color-text)', outline: 'none',
          }}
        />

        {/* Rango de fechas */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <label style={{ fontSize: 12, color: 'var(--color-muted)', whiteSpace: 'nowrap' }}>Desde</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={{
              padding: '7px 10px', borderRadius: 6, fontSize: 13,
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              color: 'var(--color-text)', outline: 'none', colorScheme: 'dark',
            }}
          />
          <label style={{ fontSize: 12, color: 'var(--color-muted)', whiteSpace: 'nowrap' }}>Hasta</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={{
              padding: '7px 10px', borderRadius: 6, fontSize: 13,
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              color: 'var(--color-text)', outline: 'none', colorScheme: 'dark',
            }}
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              title="Limpiar fechas"
              style={{
                background: 'none', border: 'none', color: 'var(--color-muted)',
                cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px',
              }}
            >×</button>
          )}
        </div>

        <ExportButton search={search} categoryId={filterCat} dateFrom={dateFrom} dateTo={dateTo} />
        <button
          onClick={() => setShowUpload(true)}
          style={{
            padding: '8px 20px', borderRadius: 6, fontSize: 14, fontWeight: 500,
            background: 'var(--color-accent)', border: 'none', color: '#fff', cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          + Subir documentos
        </button>
      </div>

      {/* Barra flotante de selección */}
      {selected.size > 0 && (
        <div style={{
          position: 'sticky', top: 16, zIndex: 30,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--color-accent)', borderRadius: 8,
          padding: '10px 18px', marginBottom: 12,
          boxShadow: '0 4px 20px rgba(14,165,233,0.35)',
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
            {selected.size} documento{selected.size !== 1 ? 's' : ''} seleccionado{selected.size !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 13,
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                color: '#fff', cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleDownloadZip}
              disabled={zipping}
              style={{
                padding: '6px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                background: zipping ? 'rgba(255,255,255,0.3)' : '#fff',
                border: 'none', color: zipping ? '#fff' : 'var(--color-accent)',
                cursor: zipping ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {zipping
                ? <><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', border: '2px solid var(--color-accent)', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} /> Preparando...</>
                : 'Descargar ZIP'
              }
            </button>
          </div>
        </div>
      )}

      {/* Tabla */}
      <div style={{
        background: 'var(--color-surface)', border: '1px solid var(--color-border)',
        borderRadius: 10, overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--color-table-header)' }}>
              {/* Checkbox seleccionar todo */}
              <th style={{
                padding: '12px 14px', width: 40, borderBottom: '1px solid var(--color-border)',
              }}>
                <input
                  type="checkbox"
                  checked={documents.length > 0 && selected.size === documents.length}
                  ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < documents.length; }}
                  onChange={toggleSelectAll}
                  style={{ cursor: 'pointer', width: 15, height: 15, accentColor: 'var(--color-accent)' }}
                  title="Seleccionar todos"
                />
              </th>
              {['Nombre', 'Categoría', 'Vinculado a', 'Tamaño', 'Estado OCR', 'Fecha', ''].map(h => (
                <th key={h} style={{
                  padding: '12px 16px', textAlign: 'left', fontSize: 11,
                  fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
                  color: 'var(--color-muted)', borderBottom: '1px solid var(--color-border)',
                  whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              : documents.length === 0
              ? (
                <tr>
                  <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--color-muted)', fontSize: 14 }}>
                    No hay documentos. Sube el primero.
                  </td>
                </tr>
              )
              : documents.map(doc => {
                const isExpanded = expandedId === doc.id;
                const hasMetadata = doc.metadata && Object.values(doc.metadata).some(v => v);
                return (
                <>
                <tr
                  key={doc.id}
                  style={{
                    borderBottom: isExpanded ? 'none' : '1px solid var(--color-border)',
                    cursor: hasMetadata ? 'pointer' : 'default',
                    background: selected.has(doc.id) ? 'rgba(14,165,233,0.06)' : 'transparent',
                  }}
                  onClick={() => hasMetadata && setExpandedId(isExpanded ? null : doc.id)}
                >
                  {/* Checkbox de selección */}
                  <td style={{ padding: '14px 14px', width: 40 }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(doc.id)}
                      onChange={() => toggleSelect(doc.id)}
                      style={{ cursor: 'pointer', width: 15, height: 15, accentColor: 'var(--color-accent)' }}
                    />
                  </td>
                  <td style={{ padding: '14px 16px', color: 'var(--color-text)', fontWeight: 500, maxWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {hasMetadata && (
                        <span style={{ color: 'var(--color-muted)', fontSize: 10, transition: 'transform 0.15s', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                          ▶
                        </span>
                      )}
                      <div>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                          {doc.nombre}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--color-muted)' }}>
                          {doc.mimetype === 'application/pdf' ? 'PDF'
                            : doc.mimetype.startsWith('image/') ? `Imagen (${doc.mimetype.split('/')[1].toUpperCase()})`
                            : doc.mimetype}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {(() => {
                      // Si tiene categoría asignada, mostrarla
                      if (doc.category_nombre) {
                        return (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500,
                            background: `${doc.category_color}22`, color: doc.category_color ?? 'var(--color-muted)',
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: doc.category_color ?? 'var(--color-muted)' }} />
                            {doc.category_nombre}
                          </span>
                        );
                      }
                      // Si no tiene categoría pero tiene metadatos de factura, mostrar "Facturas" (auto-detectado)
                      const meta = doc.metadata as Record<string, { value?: string }> | null;
                      const isInvoice = meta && (meta.numero_factura?.value || meta.base_imponible?.value || meta.total?.value);
                      if (isInvoice) {
                        return (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500,
                            background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
                            Facturas
                          </span>
                        );
                      }
                      return <span style={{ color: 'var(--color-muted)', fontSize: 12 }}>Sin categoría</span>;
                    })()}
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 13 }}>
                    {(() => {
                      // Mostrar empresa emisora del metadata si existe, sino employee_nombre
                      const meta = doc.metadata as Record<string, { value?: string }> | null;
                      const empresaEmisora = meta?.empresa_emisora?.value;
                      if (empresaEmisora) {
                        return <span style={{ color: 'var(--color-text)' }}>{empresaEmisora}</span>;
                      }
                      if (doc.employee_nombre?.trim()) {
                        return <span style={{ color: 'var(--color-text)' }}>{doc.employee_nombre}</span>;
                      }
                      return <span style={{ color: 'var(--color-muted)' }}>—</span>;
                    })()}
                  </td>
                  <td style={{ padding: '14px 16px', color: 'var(--color-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {formatBytes(doc.size_bytes)}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <OcrBadge status={doc.ocr_status} validationStatus={doc.validation_status} />
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {(() => {
                      // Mostrar fecha de factura del metadata si existe
                      const meta = doc.metadata as Record<string, { value?: string }> | null;
                      const fechaFactura = meta?.fecha_factura?.value;
                      if (fechaFactura) {
                        // Intentar parsear la fecha (formatos: dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy)
                        const parts = fechaFactura.split(/[\/\-\.]/);
                        if (parts.length === 3) {
                          const day = parseInt(parts[0], 10);
                          const month = parseInt(parts[1], 10) - 1;
                          const year = parseInt(parts[2], 10);
                          const date = new Date(year < 100 ? year + 2000 : year, month, day);
                          if (!isNaN(date.getTime())) {
                            return <span style={{ color: 'var(--color-text)' }}>{formatDate(date.toISOString())}</span>;
                          }
                        }
                        // Si no se puede parsear, mostrar el valor tal cual
                        return <span style={{ color: 'var(--color-text)' }}>{fechaFactura}</span>;
                      }
                      // Fallback a fecha de subida
                      return <span style={{ color: 'var(--color-muted)' }}>{formatDate(doc.created_at)}</span>;
                    })()}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleView(doc.id); }}
                        style={{
                          padding: '5px 12px', borderRadius: 5, fontSize: 12,
                          background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.3)',
                          color: 'var(--color-accent)', cursor: 'pointer',
                        }}
                      >
                        Ver
                      </button>
                      {doc.ocr_status === 'done' && !hasMetadata && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleReprocess(doc.id); }}
                          title="Reextraer datos del documento"
                          style={{
                            padding: '5px 12px', borderRadius: 5, fontSize: 12,
                            background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)',
                            color: '#818cf8', cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >
                          Reprocesar OCR
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                        style={{
                          padding: '5px 12px', borderRadius: 5, fontSize: 12,
                          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                          color: 'var(--color-danger)', cursor: 'pointer',
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
                {isExpanded && doc.metadata && (
                  <MetadataPanel
                    doc={doc}
                    colSpan={8}
                    onVerify={handleVerify}
                    onSaveField={handleSaveField}
                  />
                )}
                </>
                );
              })
            }
          </tbody>
        </table>
      </div>

      {/* Total */}
      {data && (
        <p style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 12 }}>
          {data.total} documento{data.total !== 1 ? 's' : ''} en total
        </p>
      )}

      {/* Modal upload */}
      {showUpload && (
        <UploadModal
          categories={categories}
          onClose={() => setShowUpload(false)}
          onDone={() => { mutate(); }}
        />
      )}

      {/* Modal de verificacion con vista dividida */}
      {verifyingDocId && (() => {
        const doc = documents.find(d => d.id === verifyingDocId);
        if (!doc) return null;
        return (
          <DocumentVerifyModal
            document={doc}
            tenantSlug={tenantSlug ?? sessionStorage.getItem('tenant_slug') ?? ''}
            onClose={() => { setVerifyingDocId(null); mutate(); }}
            onOpenExternal={() => handleViewExternal(doc.id)}
            onSaveField={async (id, field, value) => {
              await handleSaveField(id, field, value);
              mutate();
            }}
            onVerify={async (id) => {
              await handleVerify(id);
              setVerifyingDocId(null);
            }}
          />
        );
      })()}

      {/* CSS para spinner y pulse skeleton */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
